import React, { useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import { toast } from "react-toastify";
import * as Yup from 'yup';
import Webcam from 'react-webcam';

const DocUpload = (props) => {
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const webcamRef = useRef(null);

  // Define required documents based on company
  const getRequiredDocuments = () => {
    if (props?.company === 'eshram') {
      return {
        aadharFront: 'Aadhar front image is required',
        aadharBack: 'Aadhar back image is required',
        bankPassbook: 'Bank passbook image is required',
        selfie: 'Selfie is required',
      };
    }

    return {
      aadharFront: 'Aadhar front image is required',
      aadharBack: 'Aadhar back image is required',
      selfie: 'Selfie is required',
    };
  };

  const validationSchema = Yup.object().shape(
    Object.fromEntries(
      Object.entries(getRequiredDocuments()).map(([key, value]) => [
        key,
        Yup.mixed().required(value)
      ])
    )
  );

  const handleCaptureSelfie = (setFieldValue) => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    // Don't set field value yet, wait for confirmation
  };

  const handleConfirmSelfie = (setFieldValue) => {
    setFieldValue('selfie', capturedImage);
    // setShowWebcam(false);
    // setCapturedImage(null);
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleFileChange = (event, setFieldValue, field) => {
    const file = event.currentTarget.files[0];
    setFieldValue(field, file);
  };

  const toggleCamera = () => {
    setFacingMode(prevMode =>
      prevMode === "user" ? "environment" : "user"
    );
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (values) => {
    try {
      const documentUploads = {
        aadhaarfrontimg: values.aadharFront instanceof File
          ? await convertFileToBase64(values.aadharFront)
          : values.aadharFront,
        aadhaarbackimg: values.aadharBack instanceof File
          ? await convertFileToBase64(values.aadharBack)
          : values.aadharBack,
      };

      if (values.selfie) {
        documentUploads.selfieimg = values.selfie instanceof File
          ? await convertFileToBase64(values.selfie)
          : values.selfie;
      }

      // Add bank passbook for eshram
      if (props?.company === 'eshram' && values.bankPassbook) {
        documentUploads.bankPassbookimg = values.bankPassbook instanceof File
          ? await convertFileToBase64(values.bankPassbook)
          : values.bankPassbook;
      }

      if (props?.redirectToStep) {
        props.redirectToStep(documentUploads);
      } else {
        console.error("redirectToStep prop is not defined");
        toast.error("Submission handler is not configured properly");
      }
    } catch (error) {
      console.error('Error converting images:', error);
      toast.error('Error uploading documents. Please try again.');
    }
  };

  const getInitialValues = () => {
    if (props?.company === 'eshram') {
      return {
        aadharFront: null,
        aadharBack: null,
        bankPassbook: null,
        selfie: null,
      };
    }

    return {
      aadharFront: null,
      aadharBack: null,
      selfie: null,
    };
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Document Upload / ಡಾಕ್ಯುಮೆಂಟ್‌ಗಳ ಅಪ್‌ಲೋಡ್</h2>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ errors, touched, setFieldValue, values, isSubmitting }) => (
          <Form className="space-y-6">
            {/* Aadhar Front */}
            <div>
              <label className="block mb-2">Aadhar Card Front</label>
              <p className="text-sm text-gray-500">Upload the photo of front side of your Aadhar Card. / ನಿಮ್ಮ ಆಧಾರ್ ಕಾರ್ಡ್‌ನ ಮುಂಭಾಗದ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ. </p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, setFieldValue, 'aadharFront')}
                className="w-full p-2 border rounded"
              />
              {errors.aadharFront && touched.aadharFront && (
                <div className="text-red-500 text-sm mt-1">{errors.aadharFront}</div>
              )}
            </div>

            {/* Aadhar Back */}
            <div>
              <label className="block mb-2">Aadhar Card Back</label>
              <p className="text-sm text-gray-500">Upload the photo of back side of your Aadhar Card. / ನಿಮ್ಮ ಆಧಾರ್ ಕಾರ್ಡ್‌ನ ಹಿಂಭಾಗದ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, setFieldValue, 'aadharBack')}
                className="w-full p-2 border rounded"
              />
              {errors.aadharBack && touched.aadharBack && (
                <div className="text-red-500 text-sm mt-1">{errors.aadharBack}</div>
              )}
            </div>

            {/* Bank Passbook (Only for eshram) */}
            {props?.company === 'eshram' && (
              <div>
                <label className="block mb-2">Bank Passbook</label>
                <p className="text-sm text-gray-500">Uplaod the photo of first page of your bank passbook/bank statement. Please make sure that your name, bank account number and IFSC code are visible in the photo. / ನಿಮ್ಮ ಬ್ಯಾಂಕ್ ಪಾಸ್‌ಬುಕ್/ಬ್ಯಾಂಕ್ ಸ್ಟೇಟ್‌ಮೆಂಟ್‌ನ ಮೊದಲ ಪುಟದ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ. ನಿಮ್ಮ ಹೆಸರು, ಬ್ಯಾಂಕ್ ಖಾತೆ ಸಂಖ್ಯೆ ಮತ್ತು IFSC ಕೋಡ್ ಫೋಟೋದಲ್ಲಿ ಗೋಚರಿಸುತ್ತಿದೆಯೇ ಎಂಬುದನ್ನು ಖಚಿತಪಡಿಸಿಕೊಳ್ಳಿ.</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setFieldValue, 'bankPassbook')}
                  className="w-full p-2 border rounded"
                />
                {errors.bankPassbook && touched.bankPassbook && (
                  <div className="text-red-500 text-sm mt-1">{errors.bankPassbook}</div>
                )}
              </div>
            )}


            <div>
              <label className="block mb-2">Selfie</label>
              <p className="text-sm text-gray-500">Upload your selfie photo or click on the below button to take a selfie from your phone camera./ ನಿಮ್ಮ ಸೆಲ್ಫಿ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ ಅಥವಾ ನಿಮ್ಮ ಫೋನ್ ಕ್ಯಾಮೆರಾದಿಂದ ಸೆಲ್ಫಿ ತೆಗೆದುಕೊಳ್ಳಲು ಕೆಳಗಿನ ಬಟನ್ ಕ್ಲಿಕ್ ಮಾಡಿ.</p>
              {!showWebcam ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setFieldValue, 'selfie')}
                    className="w-full p-2 border rounded"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebcam(true)}
                    className="bg-[#4E05FF] text-white px-4 py-2 rounded w-full"
                  >
                    Take Selfie with Webcam
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {capturedImage ? (
                    <div className="space-y-4">
                      <img
                        src={capturedImage}
                        alt="Captured selfie"
                        className="w-64 h-64 object-cover mx-auto rounded"
                      />
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleConfirmSelfie(setFieldValue)}
                          className="bg-green-500 text-white px-6 py-2 rounded"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={handleRetake}
                          className="bg-yellow-500 text-white px-6 py-2 rounded"
                        >
                          Retake
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowWebcam(false);
                            setCapturedImage(null);
                          }}
                          className="bg-red-500 text-white px-6 py-2 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <Webcam
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          className="w-full max-w-md mx-auto rounded"
                          videoConstraints={{
                            facingMode: facingMode,
                            width: 1280,
                            height: 720
                          }}
                        />
                        <button
                          type="button"
                          onClick={toggleCamera}
                          className="absolute top-2 right-2 bg-gray-800 text-white p-2 rounded-full opacity-70 hover:opacity-100"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleCaptureSelfie(setFieldValue)}
                          className="bg-green-500 text-white px-6 py-2 rounded"
                        >
                          Capture
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowWebcam(false)}
                          className="bg-red-500 text-white px-6 py-2 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {errors.selfie && touched.selfie && (
                <div className="text-red-500 text-sm mt-1">{errors.selfie}</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#4E05FF] text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Documents'}
            </button>
          </Form>

        )}

      </Formik>
      <br />
      <p className="text-sm text-gray-500">For any help please reach out to our support team on this number 022-41498149 </p>
      <p className="text-sm text-gray-500"> ಯಾವುದೇ ಸಹಾಯಕ್ಕಾಗಿ ದಯವಿಟ್ಟು ನಮ್ಮ ಸಹಾಯ ತಂಡವನ್ನು ಈ ಸಂಖ್ಯೆಯಲ್ಲಿ ಸಂಪರ್ಕಿಸಿ: 022-41498149 </p>
    </div>
  );
};

export default DocUpload;
